import { Injectable } from '@angular/core'
import { Functions, httpsCallable } from '@angular/fire/functions'
import {
    addDoc,
    collection,
    collectionData,
    deleteDoc,
    doc,
    docData,
    Firestore,
    getDoc,
    getDocs,
    orderBy,
    query,
    serverTimestamp,
    setDoc,
    updateDoc,
    where,
} from '@angular/fire/firestore'
import { Observable } from 'rxjs'
import {
    Comment,
    FirestoreStructure,
    NearestParking,
    Parking,
    Prospect,
    ProspectContact,
    ProspectInteraction,
    ProspectInteractionType,
    ProspectScrapeRun,
    ProspectScrapeJob,
    ProspectSearchTerm,
    ProspectArea as ProspectAreaModel,
    ProspectScrapeSettings,
    ProspectSource,
    ProspectStatus,
    AdminSmtpSettings,
    CrmAssignee,
    prospectCommentsPath,
} from '@parkupp/core'
import { AuthenticationService } from './authentication.service'

export interface ProspectArea {
    slug: string
    name: string
    region: 'Cape Town' | 'Johannesburg'
    latitude: number
    longitude: number
}

@Injectable({
    providedIn: 'root',
})
export class ProspectingService {
    constructor(private firestore: Firestore, private functions: Functions, private authService: AuthenticationService) {}

    // ---------- Firestore queries ----------
    // One-shot gets for page loads (faster, no live updates).
    // Use the Observable variants (suffixed with $) only where live updates are needed (modals).

    async listProspects(): Promise<Prospect[]> {
        const ref = collection(this.firestore, FirestoreStructure.PROSPECTS)
        const snap = await getDocs(query(ref, orderBy('createdAt', 'desc')))
        return snap.docs.map((d) => ({ ...d.data(), $key: d.id } as Prospect))
    }

    getProspect(prospectKey: string): Observable<Prospect> {
        const ref = doc(this.firestore, `${FirestoreStructure.PROSPECTS}/${prospectKey}`)
        return docData(ref, { idField: '$key' }) as Observable<Prospect>
    }

    listContactsForProspect(prospectKey: string): Observable<ProspectContact[]> {
        const ref = collection(this.firestore, FirestoreStructure.PROSPECT_CONTACTS)
        return collectionData(query(ref, where('prospectKey', '==', prospectKey)), { idField: '$key' }) as Observable<ProspectContact[]>
    }

    getContact(contactKey: string): Observable<ProspectContact> {
        const ref = doc(this.firestore, `${FirestoreStructure.PROSPECT_CONTACTS}/${contactKey}`)
        return docData(ref, { idField: '$key' }) as Observable<ProspectContact>
    }

    async listScrapeRuns(): Promise<ProspectScrapeRun[]> {
        const ref = collection(this.firestore, FirestoreStructure.PROSPECT_SCRAPE_RUNS)
        const snap = await getDocs(query(ref, orderBy('createdAt', 'desc')))
        return snap.docs.map((d) => ({ ...d.data(), $key: d.id } as ProspectScrapeRun))
    }

    async updateProspectStatus(prospectKey: string, status: ProspectStatus, previousStatus?: string): Promise<void> {
        const ref = doc(this.firestore, `${FirestoreStructure.PROSPECTS}/${prospectKey}`)
        await updateDoc(ref, { status, updatedAt: serverTimestamp() })

        // Auto-log status change as interaction
        if (previousStatus && previousStatus !== status) {
            const user = this.authService.user
            await this.addInteraction({
                prospectKey,
                type: ProspectInteractionType.STATUS_CHANGE,
                summary: `Status changed from ${previousStatus} to ${status}`,
                previousStatus,
                newStatus: status,
            })
        }
    }

    async updateProspect(prospectKey: string, data: Partial<Prospect>): Promise<void> {
        const ref = doc(this.firestore, `${FirestoreStructure.PROSPECTS}/${prospectKey}`)
        const cleaned = Object.fromEntries(Object.entries(data).filter(([_, v]) => v !== undefined))
        await updateDoc(ref, { ...cleaned, updatedAt: serverTimestamp() })
    }

    async createProspect(data: Partial<Prospect>): Promise<string> {
        const ref = collection(this.firestore, FirestoreStructure.PROSPECTS)
        const docRef = await addDoc(ref, {
            ...data,
            source: ProspectSource.MANUAL,
            status: data.status || ProspectStatus.NEW,
            contactCount: 0,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        })
        return docRef.id
    }

    async createContact(data: Partial<ProspectContact>): Promise<string> {
        const ref = collection(this.firestore, FirestoreStructure.PROSPECT_CONTACTS)
        const docRef = await addDoc(ref, {
            ...data,
            source: 'MANUAL',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        })
        // Increment contact count on parent prospect
        if (data.prospectKey) {
            const prospectRef = doc(this.firestore, `${FirestoreStructure.PROSPECTS}/${data.prospectKey}`)
            const prospect = await (docData(prospectRef, { idField: '$key' }) as Observable<Prospect>).toPromise()
            if (prospect) {
                await updateDoc(prospectRef, { contactCount: (prospect.contactCount || 0) + 1, updatedAt: serverTimestamp() })
            }
        }
        return docRef.id
    }

    async updateContact(contactKey: string, data: Partial<ProspectContact>): Promise<void> {
        const ref = doc(this.firestore, `${FirestoreStructure.PROSPECT_CONTACTS}/${contactKey}`)
        await updateDoc(ref, { ...data, updatedAt: serverTimestamp() })
    }

    listComments(prospectKey: string): Observable<Comment[]> {
        const ref = collection(this.firestore, prospectCommentsPath(prospectKey))
        return collectionData(query(ref, orderBy('createdAt', 'asc')), { idField: '$key' }) as Observable<Comment[]>
    }

    async addComment(prospectKey: string, text: string): Promise<void> {
        const user = this.authService.user
        if (!user) {
            throw new Error('User must be authenticated to add comments')
        }
        const ref = collection(this.firestore, prospectCommentsPath(prospectKey))
        await addDoc(ref, {
            text,
            createdBy: user.email || 'Unknown',
            createdByName: user.displayName || user.email || 'Unknown User',
            createdAt: serverTimestamp(),
        })
    }

    // ---------- Interactions / Timeline ----------

    listInteractionsForProspect(prospectKey: string): Observable<ProspectInteraction[]> {
        const ref = collection(this.firestore, FirestoreStructure.PROSPECT_INTERACTIONS)
        return collectionData(query(ref, where('prospectKey', '==', prospectKey), orderBy('createdAt', 'desc')), {
            idField: '$key',
        }) as Observable<ProspectInteraction[]>
    }

    listInteractionsForContact(contactKey: string): Observable<ProspectInteraction[]> {
        const ref = collection(this.firestore, FirestoreStructure.PROSPECT_INTERACTIONS)
        return collectionData(query(ref, where('contactKey', '==', contactKey), orderBy('createdAt', 'desc')), {
            idField: '$key',
        }) as Observable<ProspectInteraction[]>
    }

    async listAllInteractions(): Promise<ProspectInteraction[]> {
        const ref = collection(this.firestore, FirestoreStructure.PROSPECT_INTERACTIONS)
        const snap = await getDocs(query(ref, orderBy('createdAt', 'desc')))
        return snap.docs.map((d) => ({ ...d.data(), $key: d.id } as ProspectInteraction))
    }

    async addInteraction(data: Partial<ProspectInteraction>): Promise<void> {
        const user = this.authService.user
        const ref = collection(this.firestore, FirestoreStructure.PROSPECT_INTERACTIONS)
        await addDoc(ref, {
            ...data,
            createdByUid: user?.uid || null,
            denormCreatedByName: user?.displayName || user?.email || 'Unknown',
            interactionDate: data.interactionDate || serverTimestamp(),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        })
    }

    // ---------- Scrape Jobs (new pipeline) ----------

    async listSearchTerms(): Promise<ProspectSearchTerm[]> {
        const ref = collection(this.firestore, FirestoreStructure.PROSPECT_SEARCH_TERMS)
        const snap = await getDocs(query(ref, orderBy('createdAt', 'desc')))
        return snap.docs.map((d) => ({ ...d.data(), $key: d.id } as ProspectSearchTerm))
    }

    async addSearchTerm(name: string, searchQuery: string): Promise<void> {
        const ref = collection(this.firestore, FirestoreStructure.PROSPECT_SEARCH_TERMS)
        await addDoc(ref, { name, searchQuery, enabled: true, createdAt: serverTimestamp(), updatedAt: serverTimestamp() })
    }

    async updateSearchTerm(key: string, data: Partial<ProspectSearchTerm>): Promise<void> {
        const ref = doc(this.firestore, `${FirestoreStructure.PROSPECT_SEARCH_TERMS}/${key}`)
        await updateDoc(ref, { ...data, updatedAt: serverTimestamp() })
    }

    async deleteSearchTerm(key: string): Promise<void> {
        const ref = doc(this.firestore, `${FirestoreStructure.PROSPECT_SEARCH_TERMS}/${key}`)
        await deleteDoc(ref)
    }

    async listAreas(): Promise<ProspectAreaModel[]> {
        const ref = collection(this.firestore, FirestoreStructure.PROSPECT_AREAS)
        const snap = await getDocs(query(ref))
        return snap.docs.map((d) => ({ ...d.data(), $key: d.id } as ProspectAreaModel))
    }

    async updateArea(key: string, data: Partial<ProspectAreaModel>): Promise<void> {
        const ref = doc(this.firestore, `${FirestoreStructure.PROSPECT_AREAS}/${key}`)
        await updateDoc(ref, { ...data, updatedAt: serverTimestamp() })
    }

    async listScrapeJobs(): Promise<ProspectScrapeJob[]> {
        const ref = collection(this.firestore, FirestoreStructure.PROSPECT_SCRAPE_JOBS)
        const snap = await getDocs(query(ref, orderBy('createdAt', 'desc')))
        return snap.docs.map((d) => ({ ...d.data(), $key: d.id } as ProspectScrapeJob))
    }

    getScrapeSettings(): Observable<ProspectScrapeSettings> {
        const ref = doc(this.firestore, `${FirestoreStructure.PROSPECT_SCRAPE_SETTINGS}/global`)
        return docData(ref) as Observable<ProspectScrapeSettings>
    }

    async updateScrapeSettings(data: Partial<ProspectScrapeSettings>): Promise<void> {
        const ref = doc(this.firestore, `${FirestoreStructure.PROSPECT_SCRAPE_SETTINGS}/global`)
        await setDoc(ref, data, { merge: true })
    }

    // ---------- SMTP Settings ----------

    getSmtpSettings(uid: string): Observable<AdminSmtpSettings> {
        const ref = doc(this.firestore, `${FirestoreStructure.ADMIN_SMTP_SETTINGS}/${uid}`)
        return docData(ref, { idField: '$key' }) as Observable<AdminSmtpSettings>
    }

    async saveSmtpSettings(uid: string, data: Partial<AdminSmtpSettings>): Promise<void> {
        const ref = doc(this.firestore, `${FirestoreStructure.ADMIN_SMTP_SETTINGS}/${uid}`)
        await setDoc(ref, { ...data, updatedAt: serverTimestamp() }, { merge: true })
    }

    // ---------- CRM Assignees (for prospecting) ----------

    listProspectingAssignees(): Observable<CrmAssignee[]> {
        const ref = collection(this.firestore, FirestoreStructure.CRM_ASSIGNEES)
        return collectionData(query(ref, where('prospectingEnabled', '==', true)), { idField: '$key' }) as Observable<CrmAssignee[]>
    }

    listAllAssignees(): Observable<CrmAssignee[]> {
        const ref = collection(this.firestore, FirestoreStructure.CRM_ASSIGNEES)
        return collectionData(query(ref), { idField: '$key' }) as Observable<CrmAssignee[]>
    }

    async ensureMarkAssignee(): Promise<void> {
        const ref = collection(this.firestore, FirestoreStructure.CRM_ASSIGNEES)
        const snap = await getDocs(query(ref, where('displayName', '==', 'Mark')))
        if (snap.empty) {
            await addDoc(ref, {
                displayName: 'Mark',
                badgeColor: '#0d6efd',
                active: true,
                prospectingEnabled: true,
            })
        } else {
            const existing = snap.docs[0]
            if (!existing.data()['prospectingEnabled']) {
                await updateDoc(existing.ref, { prospectingEnabled: true })
            }
        }
    }

    async updateAssigneeProspecting(key: string, enabled: boolean): Promise<void> {
        const ref = doc(this.firestore, `${FirestoreStructure.CRM_ASSIGNEES}/${key}`)
        await updateDoc(ref, { prospectingEnabled: enabled })
    }

    async assignProspect(prospectKey: string, assigneeUid: string | null, assigneeName: string | null): Promise<void> {
        const ref = doc(this.firestore, `${FirestoreStructure.PROSPECTS}/${prospectKey}`)
        await updateDoc(ref, {
            assignedToUid: assigneeUid,
            denormAssignedToName: assigneeName,
            updatedAt: serverTimestamp(),
        })
    }

    // ---------- Callable wrappers ----------

    async getConfig(): Promise<{ areas: ProspectArea[]; defaultSearchTerms: string[] }> {
        const callable = httpsCallable(this.functions, 'prospecting-getProspectingConfig')
        const result = await callable({})
        return result.data as any
    }

    async startMapsScrape(area: string, searchTerms: string[], maxPlaces: number): Promise<any> {
        const callable = httpsCallable(this.functions, 'prospecting-startMapsScrape', { timeout: 120000 })
        const result = await callable({ area, searchTerms, maxPlaces })
        return result.data
    }

    async enrichProspect(prospectKey: string, includeContacts: boolean): Promise<any> {
        const callable = httpsCallable(this.functions, 'prospecting-enrichProspect', { timeout: 300000 })
        const result = await callable({ prospectKey, includeContacts })
        return result.data
    }

    async verifyContactEmail(contactKey: string): Promise<any> {
        const callable = httpsCallable(this.functions, 'prospecting-verifyContactEmail')
        const result = await callable({ contactKey })
        return result.data
    }

    async findContactEmail(contactKey: string): Promise<any> {
        const callable = httpsCallable(this.functions, 'prospecting-findContactEmail')
        const result = await callable({ contactKey })
        return result.data
    }

    async scrapeContactLinkedIn(contactKey: string, linkedInUrl: string): Promise<any> {
        const callable = httpsCallable(this.functions, 'prospecting-scrapeContactLinkedIn', { timeout: 180000 })
        const result = await callable({ contactKey, linkedInUrl })
        return result.data
    }

    async draftOutreachEmail(contactKey: string, notes: string, senderName?: string): Promise<{ subject: string; body: string }> {
        const callable = httpsCallable(this.functions, 'prospecting-draftOutreachEmail', { timeout: 120000 })
        const result = await callable({ contactKey, notes, senderName })
        return result.data as any
    }

    async getStatus(): Promise<any> {
        const callable = httpsCallable(this.functions, 'prospecting-getProspectingStatus')
        const result = await callable({})
        return result.data
    }

    async seedConfig(): Promise<any> {
        const callable = httpsCallable(this.functions, 'prospecting-seedProspectingConfig')
        const result = await callable({})
        return result.data
    }

    async enqueueScrapeJobs(): Promise<any> {
        const callable = httpsCallable(this.functions, 'prospecting-enqueueScrapeJobs', { timeout: 120000 })
        const result = await callable({})
        return result.data
    }

    async sendOutreachEmail(data: {
        contactKey?: string
        prospectKey: string
        to: string
        subject: string
        body: string
        denormProspectName?: string
        denormContactName?: string
    }): Promise<any> {
        const callable = httpsCallable(this.functions, 'prospecting-sendOutreachEmail', { timeout: 60000 })
        const result = await callable(data)
        return result.data
    }

    async testSmtpConnection(): Promise<any> {
        const callable = httpsCallable(this.functions, 'prospecting-testSmtpConnection')
        const result = await callable({})
        return result.data
    }

    async claudeResearch(prompt: string): Promise<any> {
        const callable = httpsCallable(this.functions, 'prospecting-claudeResearch', { timeout: 120000 })
        const result = await callable({ prompt })
        return result.data
    }

    async importClaudeResults(companies: any[], prompt: string): Promise<any> {
        const callable = httpsCallable(this.functions, 'prospecting-importClaudeResults', { timeout: 120000 })
        const result = await callable({ companies, prompt })
        return result.data
    }

    // ---------- Client-side nearest parkings ----------

    computeNearestParkings(lat: number, lng: number, parkings: Parking[], limit = 5): NearestParking[] {
        return parkings
            .filter((p) => p.parkingLocation?.latitude && p.parkingLocation?.longitude)
            .map((p) => ({
                parkingKey: p.$key!,
                parkingName: p.title || 'Untitled Parking',
                latitude: p.parkingLocation.latitude,
                longitude: p.parkingLocation.longitude,
                distanceKm: this.haversineKm(lat, lng, p.parkingLocation.latitude, p.parkingLocation.longitude),
            }))
            .sort((a, b) => a.distanceKm - b.distanceKm)
            .slice(0, limit)
            .map((p) => ({ ...p, distanceKm: Math.round(p.distanceKm * 10) / 10 }))
    }

    private haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
        const toRad = (deg: number) => (deg * Math.PI) / 180
        const R = 6371
        const dLat = toRad(lat2 - lat1)
        const dLng = toRad(lng2 - lng1)
        const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
        return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    }
}
